import { useState, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import { useStore } from '../../store/useStore';
import type { WindowState } from '../../types';
import { WindowContent } from './WindowContent';
import {
  TerminalSquare, Folder, Clipboard, MessageSquarePlus, Settings,
  Globe, PlaySquare, Monitor, Minus, Square, X, Calculator, Bomb,
} from 'lucide-react';

const APP_ICONS: Record<string, React.ReactNode> = {
  terminal: <TerminalSquare className="w-3.5 h-3.5 text-cyan-400" />,
  'file-manager': <Folder className="w-3.5 h-3.5 text-amber-400" />,
  projects: <Clipboard className="w-3.5 h-3.5 text-purple-400" />,
  editor: <Clipboard className="w-3.5 h-3.5 text-green-400" />,
  chat: <MessageSquarePlus className="w-3.5 h-3.5 text-blue-400" />,
  settings: <Settings className="w-3.5 h-3.5 text-slate-300" />,
  browser: <Globe className="w-3.5 h-3.5 text-pink-400" />,
  preview: <PlaySquare className="w-3.5 h-3.5 text-emerald-400" />,
  calculator: <Calculator className="w-3.5 h-3.5 text-orange-400" />,
  minesweeper: <Bomb className="w-3.5 h-3.5 text-red-400" />,
};

export function WindowManager({ windows }: { windows: WindowState[] }) {
  return (
    <>
      {windows.map((win) => (
        <Window key={win.id} window={win} />
      ))}
    </>
  );
}

function Window({ window: win }: { window: WindowState }) {
  const focusWindow = useStore((s) => s.focusWindow);
  const closeWindow = useStore((s) => s.closeWindow);
  const minimizeWindow = useStore((s) => s.minimizeWindow);
  const maximizeWindow = useStore((s) => s.maximizeWindow);
  const updateWindow = useStore((s) => s.updateWindow);
  const activeWindowId = useStore((s) => s.activeWindowId);
  const [dragging, setDragging] = useState(false);

  const isActive = activeWindowId === win.id;
  const isFullscreen = win.isMaximized;

  const handleDragStart = useCallback(() => {
    setDragging(true);
    focusWindow(win.id);
  }, [focusWindow, win.id]);

  const handleDragStop = useCallback(
    (_e: any, d: { x: number; y: number }) => {
      setDragging(false);
      updateWindow(win.id, { x: d.x, y: d.y });
    },
    [updateWindow, win.id]
  );

  const handleResizeStop = useCallback(
    (_e: any, _dir: any, ref: HTMLElement, delta: { width: number; height: number }, position: { x: number; y: number }) => {
      updateWindow(win.id, {
        width: parseInt(ref.style.width),
        height: parseInt(ref.style.height),
        x: position.x,
        y: position.y,
      });
    },
    [updateWindow, win.id]
  );

  if (win.isMinimized) return null;

  const content = (
    <div className="flex flex-col h-full w-full">
      {/* Title bar */}
      <div
        className={`flex items-center justify-between h-8 px-3 shrink-0 select-none ${
          isActive
            ? 'bg-desktop-surface border-b border-desktop-border'
            : 'bg-desktop-surface/80 border-b border-desktop-border/60'
        }`}
        onMouseDown={() => focusWindow(win.id)}
        onTouchStart={() => focusWindow(win.id)}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          {APP_ICONS[win.appType]}
          <span className="text-xs font-medium text-desktop-text/90 truncate">{win.title}</span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            onClick={() => minimizeWindow(win.id)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors"
            title="Minimize"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => maximizeWindow(win.id)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors"
            title={isFullscreen ? 'Restore' : 'Maximize'}
          >
            <Square className="w-3 h-3" />
          </button>
          <button
            onClick={() => closeWindow(win.id)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-600 hover:text-white text-desktop-text/70 transition-colors"
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-hidden bg-desktop-surface2">
        <WindowContent window={win} />
      </div>
    </div>
  );

  if (isFullscreen) {
    return (
      <div
        className={`absolute inset-x-0 top-0 bottom-0 pointer-events-auto z-[${win.zIndex}] ${isActive ? '' : 'opacity-90'}`}
        style={{ zIndex: win.zIndex }}
        onMouseDown={() => focusWindow(win.id)}
      >
        {content}
      </div>
    );
  }

  return (
    <Rnd
      size={{ width: win.width, height: win.height }}
      position={{ x: win.x, y: win.y }}
      onDragStart={handleDragStart}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      minWidth={win.minWidth}
      minHeight={win.minHeight}
      bounds="parent"
      dragHandleClassName="drag-handle"
      style={{ zIndex: win.zIndex }}
      className={dragging ? 'window-dragging pointer-events-auto' : 'pointer-events-auto'}
      onMouseDown={() => focusWindow(win.id)}
      onTouchStart={() => focusWindow(win.id)}
      resizeHandleStyles={{
        bottomRight: { cursor: 'nwse-resize', width: '16px', height: '16px' },
        bottomLeft: { cursor: 'nesw-resize', width: '16px', height: '16px' },
        topRight: { cursor: 'nesw-resize', width: '16px', height: '16px' },
        topLeft: { cursor: 'nwse-resize', width: '16px', height: '16px' },
      }}
      enableResizing={{
        top: true, right: true, bottom: true, left: true,
        topRight: true, topLeft: true, bottomRight: true, bottomLeft: true,
      }}
    >
      <div
        className={`h-full w-full rounded-lg overflow-hidden border shadow-2xl animate-window-open transition-shadow ${
          isActive
            ? 'border-desktop-border shadow-black/50 ring-1 ring-desktop-accent/20'
            : 'border-desktop-border/60 shadow-black/30'
        }`}
      >
        <div className="drag-handle h-full w-full flex flex-col">
          {content}
        </div>
      </div>
    </Rnd>
  );
}